import * as React from 'react'
import { clsx } from 'clsx'
import {
  ExternalLinkIcon,
  FileIcon,
  InfoCircledIcon,
  MagnifyingGlassIcon,
  SpeakerLoudIcon,
  UploadIcon,
  VideoIcon,
} from '@radix-ui/react-icons'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import * as Dialog from '~/sotano-kit/primitives/dialog'
import { RepoFile, useRepoFiles } from '~/hooks-data/use-repo-files'
import { Input } from '~/sotano-kit/primitives/input'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import type { MediaBlock } from '@basehub/data/replicache/index'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'

interface SelectFromThisRepoModalProps {
  children: React.ReactNode
  onUpload: VoidFunction
  onSelect: (data: RepoFile) => void
  constraints: MediaBlock['constraints']['allowedMimeTypes']
}

export const SelectMediaDialog = ({
  children,
  onSelect,
  onUpload,
  constraints,
}: SelectFromThisRepoModalProps) => {
  const data = useRepoFiles()
  const [selectedFileKey, setSelectedFileKey] = React.useState<string | null>(
    null
  )
  const [activeFilter, setActiveFilter] = React.useState<
    'all' | 'image' | 'video' | 'audio' | 'file'
  >('all')
  const [searchQuery, setSearchQuery] = React.useState('')

  const allowedTypes = React.useMemo(() => {
    let allAllowed = true
    if (!constraints) return ['*/*']
    const acc: string[] = []
    for (const [key, val] of Object.entries(constraints)) {
      if (val !== true) {
        allAllowed = false
        val && acc.push(...val)
      } else {
        acc.push(`${key}/*`)
      }
    }

    if (allAllowed) {
      return ['*/*']
    } else {
      return acc
    }
  }, [constraints])

  React.useEffect(() => {
    if (allowedTypes.length === 1) {
      const type = allowedTypes[0]
      if (type && type.startsWith('image/')) {
        setActiveFilter('image')
      } else if (type && type.startsWith('video/')) {
        setActiveFilter('video')
      } else if (type && type.startsWith('audio/')) {
        setActiveFilter('audio')
      } else if (type && !type.startsWith('*/')) {
        setActiveFilter('file')
      }
    }
  }, [allowedTypes])

  const filteredFiles: (RepoFile & { isAllowed: boolean })[] =
    React.useMemo(() => {
      if (!data?.length) return []

      return data.reduce(
        (acc, file) => {
          // Check if file matches search query
          if (
            searchQuery &&
            !(
              file.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
              file.altText?.toLowerCase().includes(searchQuery.toLowerCase())
            )
          ) {
            return acc
          }

          // Check if file type is allowed
          let isAllowed = true
          if (
            allowedTypes &&
            allowedTypes.length > 0 &&
            allowedTypes[0] !== '*/*'
          ) {
            isAllowed = false
            for (const allowedType of allowedTypes) {
              if (allowedType && allowedType.includes('*')) {
                const typePrefix = allowedType.split('*')[0]
                if (
                  (typePrefix === 'image/' && file.type === 'image') ||
                  (typePrefix === 'video/' && file.type === 'video') ||
                  (typePrefix === 'audio/' && file.type === 'audio') ||
                  (typePrefix === 'application/' && file.type === 'file')
                ) {
                  isAllowed = true
                  break
                }
              }
            }
          }

          // Check if file matches active filter
          if (activeFilter !== 'all' && file.type !== activeFilter) {
            return acc
          }

          return [...acc, { ...file, isAllowed }]
        },
        [] as (RepoFile & { isAllowed: boolean })[]
      )
    }, [data, activeFilter, searchQuery, allowedTypes])

  const [isOpen, setIsOpen] = React.useState(false)

  const handleSelect = React.useCallback(async () => {
    const selectedFile = filteredFiles.find(
      (file) => file.key === selectedFileKey
    )
    if (selectedFile) {
      onSelect(selectedFile)
      setIsOpen(false)
      setSelectedFileKey(null)
    }
  }, [filteredFiles, onSelect, selectedFileKey])

  React.useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        handleSelect()
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleSelect, isOpen])

  return (
    <Dialog.Dialog open={isOpen} onOpenChange={(o) => setIsOpen(o)}>
      <Dialog.DialogTrigger asChild>{children}</Dialog.DialogTrigger>
      <Dialog.DialogContent
        overlayProps={{
          onClick: () => setIsOpen(false),
        }}
        className="w-full !max-w-screen-md !pb-6 !pt-4 [&>hr:last-of-type]:mt-0"
      >
        <Dialog.DialogHeader divider className="!-mb-1">
          <Dialog.DialogTitle>Select media from this repo</Dialog.DialogTitle>
        </Dialog.DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <div className="flex space-x-2 overflow-x-auto p-px -ml-px">
            {(['all', 'image', 'video', 'audio', 'file'] as const).map(
              (filterType) => (
                <Button
                  key={filterType}
                  unstyled
                  focusStyled
                  className={clsx(
                    'px-3 leading-[1.625rem] font-medium text-xs rounded-md border capitalize-first transition-[opacity,color,background-opacity,background-color] cursor-default',
                    'focus-visible:outline-offset-0',
                    activeFilter === filterType
                      ? 'bg-dark-control-base text-dark-control-base bg-opacity-15 border-dark-control-base'
                      : 'opacity-7 hover:bg-dark-shade text-dark-faint border-dark-border-solid'
                  )}
                  onClick={() => setActiveFilter(filterType)}
                >
                  {filterType}
                </Button>
              )
            )}
          </div>

          <Input
            autoFocus
            type="text"
            placeholder="Search"
            size="xs"
            containerClassName={clsx(
              'grow max-w-xs transition-colors',
              searchQuery ? 'text-dark-bright' : 'text-dark-faint'
            )}
            className="placeholder:text-dark-faint !border-dark-elevated focus-visible:!border-dark-control-base "
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<MagnifyingGlassIcon height={14} width={14} />}
          />
        </div>

        {!data ? (
          <div className="text-dark-faint text-sm font-medium leading-normal flex items-center justify-center h-[18.5rem] animate-breathing">
            No media uploaded
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-dark-faint text-sm font-medium leading-normal flex items-center justify-center h-[18.5rem]">
            No matching results
          </div>
        ) : (
          <ScrollArea
            className={clsx(
              'max-h-[18.5rem] h-[18.5rem] -mr-3 pr-3',
              'after:w-full after:absolute after:h-3 after:bg-gradient-dark after:top-0 after:left-0'
            )}
            viewportClassName="pb-2 pt-2"
          >
            <div className="grid grid-cols-2 gap-2">
              {filteredFiles.map((file) => {
                return (
                  <div
                    key={file.key}
                    className={clsx(
                      'group/card',
                      'grid grid-cols-[auto_1fr_auto] p-2 gap-x-2 grid-rows-2 items-center rounded-lg overflow-hidden bg-dark-base-dark cursor-default border transition-colors duration-150 text-dark-faint text-xs hover:bg-dark-shade select-none',
                      selectedFileKey === file.key
                        ? 'border-dark-control-base'
                        : 'border-dark-border-solid'
                    )}
                    onClick={() =>
                      setSelectedFileKey((prev) =>
                        prev === file.key ? null : file.key
                      )
                    }
                  >
                    <div className="size-11 flex-shrink-0 overflow-hidden row-span-full mr-1 rounded bg-dark-bg text-dark-bright relative">
                      {file.type === 'image' ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={file.url + '?w=44&h=44&fit=cover'}
                          alt={file.altText ?? ''}
                          className="w-full h-full object-cover"
                        />
                      ) : file.type === 'video' ? (
                        <VideoIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      ) : file.type === 'audio' ? (
                        <SpeakerLoudIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      ) : (
                        <FileIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      )}
                    </div>

                    <div className="text-sm font-medium text-dark-bright flex items-center justify-between col-span-2">
                      <span className="truncate">{file.fileName}</span>
                      {file.isAllowed || (
                        <SimpleTooltip
                          content="This file type is not allowed."
                          contentClassName="!z-[111]"
                        >
                          <InfoCircledIcon className="text-dark-error" />
                        </SimpleTooltip>
                      )}
                    </div>
                    <span className="text-xs font-medium">{file.size}</span>
                    <ButtonLink
                      unstyled
                      focusStyled
                      href={file.url}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs font-medium hover:text-dark-bright text-dark-faint transition-[color_opacity] group-hover/card:opacity-100 group-hover/card:visible invisible opacity-0 cursor-default flex items-center gap-1"
                    >
                      <span>Preview</span>
                      <ExternalLinkIcon height={14} width={14} />
                    </ButtonLink>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        )}

        <Dialog.DialogFooter
          divider
          className="!justify-start items-center overflow-hidden"
        >
          <Button
            intent="secondary"
            className="shrink-0"
            icon={<UploadIcon />}
            onClick={onUpload}
          >
            Upload new
          </Button>
          {selectedFileKey && (
            <div className="hidden sm:block text-xs text-dark-faint truncate flex-1 mr-2">
              Selected:{' '}
              <span className="text-dark-bright">
                {selectedFileKey.split('/').pop()}
              </span>
            </div>
          )}
          <Dialog.DialogClose asChild>
            <Button intent="secondary" className="sm:!ml-auto">
              Cancel
            </Button>
          </Dialog.DialogClose>
          <Dialog.DialogClose asChild>
            <Button
              intent="primary"
              disabled={!selectedFileKey}
              onClick={handleSelect}
            >
              Select
            </Button>
          </Dialog.DialogClose>
        </Dialog.DialogFooter>
      </Dialog.DialogContent>
    </Dialog.Dialog>
  )
}
